import { memo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { classNames } from '~/utils/classNames';
import type { TaskItem, ProgressAnnotation } from '~/types/context';

interface BuildActivityProps {
  /** The task_manager plan for the current turn — the coarse timeline spine. */
  tasks: TaskItem[];
  /** Per-tool progress labels (BUILT_IN_TOOL_LABELS). The latest drives the live line. */
  progress?: ProgressAnnotation[];
  handleStop?: () => void;
}

/*
 * BuildActivity — the live "watch it build" panel shown while a turn streams.
 *
 * Two tiers over data the turn already emits, so a 40-tool build reads as a
 * calm timeline instead of a wall:
 *   1. PLAN SPINE  — the task_manager TaskItem[] (done ✓ / active / pending / failed).
 *   2. LIVE ACTION — the latest per-tool ProgressAnnotation label + message, shown
 *      under the active task; the granular calls roll up into a count, not a list.
 *
 * Replaces the stacked ProgressCompilation + TaskProgress + WorkingBanner. Tier 1
 * is live today; Tier 2 lights up once progressAnnotations are wired from the
 * stream (BaseChat currently passes []). All motion is gated on reduced-motion.
 */
export const BuildActivity = memo(({ tasks, progress = [], handleStop }: BuildActivityProps) => {
  const reduce = useReducedMotion();

  const done = tasks.filter((t) => t.status === 'done').length;
  const total = tasks.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const latest = progress.length > 0 ? progress[progress.length - 1] : undefined;
  const hasLiveMessage = !!latest?.message && latest.message !== latest.label;

  return (
    <div className="w-full max-w-chat mx-auto bg-surface border border-hairline rounded-[14px] overflow-hidden">
      {/* header */}
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-hairline">
        <span className="i-svg-spinners:90-ring-with-bg text-accent text-[13px] flex-shrink-0" />
        <span className="font-geist text-[12.5px] font-medium text-primary">Building your site</span>
        {total > 0 && <span className="font-geist-mono text-[11px] text-accent ml-1">{pct}%</span>}
        {handleStop && (
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              handleStop();
            }}
            className="ml-auto flex-shrink-0 inline-flex items-center gap-1.5 rounded-md border-0 bg-transparent px-1.5 py-0.5 font-geist text-[12px] cursor-pointer transition-colors text-muted hover:text-danger touch:min-h-9 touch:px-3"
          >
            <span className="i-ph:stop-fill text-[12px]" />
            <span>Stop</span>
          </button>
        )}
      </div>

      {/* Tier 1: plan spine (falls back to just the live action when no plan yet) */}
      {total > 0 ? (
        // On phones/tablets a long plan scrolls inside the panel instead of covering the thread.
        <div className="px-4 py-2 touch:max-h-[40dvh] touch:overflow-y-auto touch:overscroll-contain">
          {tasks.map((task, i) => {
            const isDone = task.status === 'done';
            const isNow = task.status === 'in-progress';
            const isFailed = task.status === 'failed';
            const isLast = i === tasks.length - 1;

            return (
              <div key={task.id} className="relative flex items-start gap-3 py-1.5">
                {!isLast && <span className="absolute left-[6px] top-[22px] bottom-[-2px] w-px bg-hairline" />}
                <span
                  className={classNames(
                    'flex-shrink-0 w-[13px] h-[13px] rounded-full mt-[1px] grid place-items-center',
                    isDone && 'bg-success/15',
                    isNow && 'bg-accent/15',
                    isFailed && 'bg-danger/15',
                    !isDone && !isNow && !isFailed && 'border border-subtle',
                  )}
                >
                  {isDone && <span className="i-ph:check-bold text-success text-[8px]" />}
                  {isFailed && <span className="i-ph:x-bold text-danger text-[8px]" />}
                  {isNow && (
                    <span
                      className={classNames('w-[5px] h-[5px] rounded-full bg-accent', !reduce && 'animate-pulse')}
                    />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div
                    className={classNames(
                      'font-geist text-[12.5px] leading-snug',
                      isNow
                        ? 'text-primary font-medium'
                        : isDone
                          ? 'text-secondary'
                          : isFailed
                            ? 'text-danger'
                            : 'text-muted',
                    )}
                  >
                    {task.title}
                  </div>
                  {isNow && latest && (
                    <div className="flex items-center gap-2 mt-1 flex-wrap min-w-0">
                      <span className="font-geist text-[11.5px] text-accent">{latest.label}</span>
                      {hasLiveMessage && (
                        <span className="font-geist-mono text-[10.5px] text-faint truncate">{latest.message}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="px-4 py-3 flex items-center gap-2 min-w-0">
          <span
            className={classNames('w-[5px] h-[5px] rounded-full bg-accent flex-shrink-0', !reduce && 'animate-pulse')}
          />
          <span className="font-geist text-[12.5px] text-primary flex-shrink-0">
            {latest?.label ?? 'Getting started…'}
          </span>
          {hasLiveMessage && (
            <span className="font-geist-mono text-[10.5px] text-faint truncate">{latest?.message}</span>
          )}
        </div>
      )}

      {/* progress bar */}
      {total > 0 && (
        <div className="h-[3px] bg-sunken mx-4 mb-2 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-accent"
            style={{ boxShadow: '0 0 10px var(--accent-glow)' }}
            initial={false}
            animate={{ width: `${pct}%` }}
            transition={reduce ? { duration: 0 } : { duration: 0.6, ease: 'easeOut' }}
          />
        </div>
      )}

      {/* roll-up: the granular tool calls, summarized not listed */}
      {progress.length > 0 && (
        <div className="px-4 py-2.5 border-t border-hairline font-geist-mono text-[10.5px] text-muted">
          {progress.length} {progress.length === 1 ? 'action' : 'actions'}
        </div>
      )}
    </div>
  );
});

BuildActivity.displayName = 'BuildActivity';
